import axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import type { LegacyApiResponse } from "../types/api";
import {
  isOperatorTwoFactorSetupRequired,
  isHostAnswerableRefusal,
  noteIfConfirmationDemanded,
  isStepUpErrorCode,
} from "./stepUp";
import { installStaffConfirmation } from "./staffConfirmation/bindStaffConfirmation";
import {
  canHandOffToStepUpHost,
  challengeStepUp,
  stepUpCodeHeader,
} from "./stepUpPromptBroker";
import { noteOperatorSetupRequired } from "./operatorTwoFactorSignal";
import { resolveApiBaseUrl } from "./apiUrl";
import { normaliseQueryForApi, serializeApiParams } from "./apiQueryEncoding";
import {
  replayRequest,
  requestLoginTwoFactorClearance,
  shouldReplayAfterLoginTwoFactor,
} from "./loginTwoFactorGate";
import { currentBearer } from "../auth/tokenProvider";
import {
  bearerForReplay,
  isRefusedBearer,
  replayConfig,
} from "../auth/unauthorizedReplay";

type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;

export type QueryParams = Record<string, QueryValue>;
export type RequestBody =
  | Record<string, unknown>
  | FormData
  | string
  | null
  | undefined;

interface V2ErrorResponseBody {
  success: false;
  message: string;
  code?: string;
  /** See {@link reference} in the interceptor — the api's validation trace id. */
  reference?: string;
  /** See {@link conflictReason} in the interceptor — why a handle 409 happened. */
  reason?: string;
  /** Structured extras, e.g. a refused TOTP code's `reason` ("already_used"). */
  details?: unknown;
}

interface LegacyErrorResponseBody {
  error?: string;
  message?: string;
  code?: string;
}

type ErrorResponseBody = V2ErrorResponseBody | LegacyErrorResponseBody;

interface AxiosHeadersDefaults {
  common?: Record<string, string>;
  delete?: Record<string, string>;
  get?: Record<string, string>;
  patch?: Record<string, string>;
  post?: Record<string, string>;
  put?: Record<string, string>;
}

interface SafeAxiosDefaults {
  baseURL?: string;
  headers?: AxiosHeadersDefaults;
}

const toSearchParamsRecord = (params: QueryParams): Record<string, string> => {
  const entries = Object.entries(params).flatMap(([key, value]) => {
    if (value === null || value === undefined) {
      return [];
    }

    if (Array.isArray(value)) {
      return [[key, value.join(",")]];
    }

    return [[key, String(value)]];
  });

  return Object.fromEntries(entries);
};

/**
 * Legacy web-storage keys, purged on boot (T-184).
 *
 * `ubold_user` held the Auth0 ID-token claims object — including `__raw`, the raw
 * signed JWT — in sessionStorage, and `authId` held the Auth0 subject in
 * localStorage. NEITHER was ever read back: the only writer was AuthContext, and
 * the accessors around them were referenced by nothing but their own tests. The
 * console was persisting a credential for no purpose at all, where any script on
 * the page could read it.
 *
 * Deleting the writes does not clear what is already in a browser. sessionStorage
 * dies with the tab, but a console tab left open for days keeps it, and `authId`
 * in localStorage survives logout entirely — `performLogout` only clears
 * sessionStorage. So the keys are removed on load as well as no longer written.
 */
const LEGACY_AUTH_STORAGE_KEYS = ["ubold_user"] as const;
const LEGACY_LOCAL_STORAGE_KEYS = ["authId"] as const;

export const purgeLegacyAuthStorage = (): void => {
  for (const key of LEGACY_AUTH_STORAGE_KEYS) {
    sessionStorage.removeItem(key);
  }
  for (const key of LEGACY_LOCAL_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
};

purgeLegacyAuthStorage();

const getSafeAxiosDefaults = (): SafeAxiosDefaults => {
  const axiosWithDefaults = axios as typeof axios & {
    defaults?: SafeAxiosDefaults;
  };

  if (!axiosWithDefaults.defaults) {
    axiosWithDefaults.defaults = {
      headers: {},
    } as typeof axios.defaults & SafeAxiosDefaults;
  }

  const defaults = axiosWithDefaults.defaults;
  const safeHeaders = (defaults.headers ??
    {}) as typeof axios.defaults.headers & AxiosHeadersDefaults;

  defaults.headers = safeHeaders;
  defaults.headers.common = defaults.headers.common ?? {};
  defaults.headers.post = defaults.headers.post ?? {};

  return defaults;
};

// Use API_URL for backend API requests, not BASE_URL (which is for frontend
// redirects). resolveApiBaseUrl falls back to the origin's stable API host when
// the build-time value is missing or a non-canonical (Railway preview) host —
// see src/services/apiUrl.ts.
const apiURL = resolveApiBaseUrl();
// Set default content type and base URL for axios
const axiosDefaults = getSafeAxiosDefaults();
axiosDefaults.headers.post["Content-Type"] = "application/json";
axiosDefaults.baseURL = apiURL;

// 🔴 CONFIRM WITH GOOGLE: swap a confirmation handle for a staff confirmation
// bound to the exact request (services/staffConfirmation/bindStaffConfirmation).
// Installed BEFORE every other request interceptor on purpose: axios runs them
// last-added first, so this one runs last, once the URL and headers are final.
installStaffConfirmation(axios);

// Every query string leaves in a form the api's request validator accepts: a
// space as %20 rather than "+", and no raw ! ' ( ) * (see apiQueryEncoding.ts).
// A search for "sam thomas" failed with "The request didn't go through" before
// this. The interceptor covers URLs built by hand; the serializer covers axios
// `params`, whose own encoder has the same flaw.
(
  axios as unknown as { defaults?: { paramsSerializer?: unknown } }
).defaults!.paramsSerializer = {
  serialize: serializeApiParams,
};
axios.interceptors?.request?.use?.((config) => {
  if (typeof config.url === "string")
    config.url = normaliseQueryForApi(config.url);
  return config;
});

// 🔴 THE BEARER, PER REQUEST. Every request leaves with the session's CURRENT
// token from the provider (src/auth/tokenProvider.ts), asked for at send time.
// It used to be written onto `defaults.headers.common` once at sign-in and
// never renewed, so the first request after it expired read as "Invalid
// credentials", and every client that bypasses these interceptors copied the
// same stale header out of the defaults. Without a session, or when the source
// cannot produce a token, the request leaves without a bearer (a replay drops
// the one it was first sent with) and the api's 401 is the answer, as it
// always was.
axios.interceptors?.request?.use?.(async (config) => {
  const bearer = await currentBearer();
  if (bearer) config.headers.set("Authorization", bearer);
  else config.headers.delete("Authorization");
  return config;
});
//const testUser = 'auth0|66d95413e1ac983f011fb379'
//axios.defaults.baseURL = "https://80eight.zilo-aws.co.za/"

/**
 * The headers a request went out with, as a plain record. axios 1.x hands the
 * config back with an AxiosHeaders instance; a re-send that spreads it would lose
 * the entries behind its accessors, and X-2FA-Code has to be ADDED to them, not
 * replace them (Authorization, Idempotency-Key and the view-as header ride here).
 */
const plainHeaders = (headers: unknown): Record<string, string> => {
  if (!headers || typeof headers !== "object") return {};
  const asJson = (headers as { toJSON?: () => unknown }).toJSON;
  const source = typeof asJson === "function" ? asJson.call(headers) : headers;
  const out: Record<string, string> = {};
  Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
    if (typeof value === "string") out[key] = value;
    else if (typeof value === "number" || typeof value === "boolean")
      out[key] = String(value);
  });
  return out;
};

/** How long the step-up prompt waits for a re-send (matches moneyOpRequest). */
const STEP_UP_RESEND_TIMEOUT_MS = 20000;

/**
 * The api's own words for a refusal — v2 `message`, else legacy `error` — or ""
 * when it sent none. A blank string counts as none: `"   "` is truthy, and
 * showing an operator an empty toast is worse than the generic copy.
 */
const forbiddenReasonOf = (data: ErrorResponseBody | undefined): string => {
  const message = (data as { message?: unknown } | undefined)?.message;
  if (typeof message === "string" && message.trim()) return message;
  const legacy = (data as { error?: unknown } | undefined)?.error;
  return typeof legacy === "string" && legacy.trim() ? legacy : "";
};

// Response interceptor - handles both v2 and legacy response/error formats
axios.interceptors?.response?.use?.(
  (response: AxiosResponse) => {
    // Check for v2 error responses that arrive with a 2xx status
    const body = response.data as Record<string, unknown> | undefined;
    if (body && "success" in body && body.success === false) {
      const errorMessage =
        (typeof body.message === "string" ? body.message : "") ||
        "Request failed";
      return Promise.reject(new Error(errorMessage));
    }
    return response;
  },
  function onResponseError(
    error: AxiosError<ErrorResponseBody>,
    mayReplay = true
  ): Promise<AxiosResponse> {
    // 🔴 SIGN-IN TWO-FACTOR, before anything else reads the refusal. A 403
    // LOGIN_TWO_FACTOR_REQUIRED means this bearer has not passed the login code
    // yet: the operator owes a code, not a permission, so it must never read as
    // "Access Forbidden" or reach /unauthorized. Every such refusal waits on ONE
    // code screen (the AuthProvider's) and is then sent again, once; the replay
    // runs through this interceptor too, so its own failure arrives shaped. With
    // no screen mounted, or the screen abandoned, the original refusal is
    // handled below like any other 403, its code and status kept.
    if (mayReplay) {
      return shouldReplayAfterLoginTwoFactor(error).then((replay) =>
        replay
          ? requestLoginTwoFactorClearance().then(
              () => replayRequest(axios, error),
              () => onResponseError(error, false)
            )
          : onResponseError(error, false)
      );
    }

    if (!error.response) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    // Preserve the server error `code` on the rejected Error so callers (e.g. the
    // admin step-up flow) can react to TWO_FACTOR_REQUIRED / INVALID_TWO_FACTOR_CODE.
    const code = typeof data?.code === "string" ? data.code : undefined;
    // 🔴 AND THE VALIDATION REFERENCE. The api answers a schema rejection with
    // `{"success":false,"message":"Validation failed"}` and NOTHING else — it
    // withholds the field list in production on purpose, because the validator
    // runs above `authenticate` and the field paths would leak the internal
    // spec to anonymous callers (api-v2#1713). What it does return is a
    // `reference`, the same short id it writes to its own log beside the field
    // that actually failed.
    //
    // Without this, an operator sees "Validation failed", naming no field, and
    // there is no way to connect their report to the log line that explains it.
    // That is not hypothetical: it cost an afternoon on the press editor's
    // embargo, and a second round afterwards when the first fix turned out not
    // to be the one that was biting.
    //
    // It rides on the MESSAGE, not just as a property, because every surface in
    // this console shows `err.message` and nothing else — attaching a field
    // nobody reads would fix this in principle and not on screen. Only a
    // response that carries one gets the suffix, so nothing else gets noisier.
    const reference =
      typeof (data as V2ErrorResponseBody)?.reference === "string"
        ? (data as V2ErrorResponseBody).reference
        : undefined;
    // 🔴 AND THE STATUS, because without it a 403 is indistinguishable from a
    // dead network by the time a screen sees it.
    //
    // Everything below collapses the response to a message string. A list page
    // catching `Error("Access Forbidden")` cannot tell a permission denial from
    // a timeout, so every newsroom list rendered "The request didn't go
    // through" while the server was plainly answering "Permission denied" —
    // which sent a real investigation looking for an outage. Carrying `status`
    // lets a screen say the true thing without matching on copy, and message
    // strings are the one part of this that gets rewritten freely.
    // 🔴 AND THE HANDLE CONFLICT REASON. The api answers three different
    // situations with one 409 code (HANDLE_TAKEN, or CONFLICT on the personal
    // claim): somebody else holds the handle, the caller's own handles changed
    // during the save, or somebody else is claiming it right now. It now says
    // which in a machine-readable `reason` beside `code`, so a screen can tell
    // "pick another handle" from "reload" from "try again in a moment" without
    // matching on the wording. Only handle conflicts carry one.
    const conflictReason =
      typeof (data as V2ErrorResponseBody)?.reason === "string"
        ? (data as V2ErrorResponseBody).reason
        : undefined;
    // 🔴 AND THE STEP-UP DETAILS. A refused TOTP code carries `details.reason`
    // ("already_used", "wrong_code", "clock_drift", …); "already used" is the
    // one an operator can act on differently (wait for the next code), so the
    // detail rides along for isStepUpCodeAlreadyUsed. Only an object is kept.
    const rawDetails = (data as V2ErrorResponseBody | undefined)?.details;
    const details =
      typeof rawDetails === "object" && rawDetails !== null
        ? (rawDetails as Record<string, unknown>)
        : undefined;
    const rejectWithCode = (message: string): Promise<never> =>
      Promise.reject(
        Object.assign(
          new Error(reference ? `${message} (ref ${reference})` : message),
          code ? { code, status } : { status },
          reference ? { reference } : {},
          conflictReason ? { reason: conflictReason } : {},
          details ? { details } : {}
        )
      );

    // 🔴 THE OPERATOR'S OWN STEP-UP, FOR A SCREEN WITH NO CODE FIELD (ENG-1378).
    // "Set up your authenticator first" (403 ADMIN_TWO_FACTOR_SETUP_REQUIRED /
    // 400 TWO_FACTOR_NOT_ENABLED) and "enter your code" (403 TWO_FACTOR_REQUIRED)
    // are both actionable in place. A request that opted in (`stepUp: "host"` on
    // its config, never a header) is handed to the one prompt host instead of
    // being rejected: the host gets the operator enrolled and asks for the first
    // code, and the IDENTICAL request goes again with X-2FA-Code. The caller's
    // promise settles with that answer. Re-sending is safe because the gate
    // refuses before the dual-control leg and the controller run — nothing was
    // filed or executed. The re-send is marked so its own answer never comes
    // back here, and the enrolment calls themselves are never handed off, so
    // set-up cannot loop. Everything that did not opt in is rejected exactly as
    // before; the ~30 callers with their own code field never see this.
    const refusal = { code, status };
    // Whatever the caller does with it, "this operator has no authenticator" is
    // a fact about the operator: publish it, so every code field on the page
    // gives way to the set-up prompt even when the signed-in profile is stale
    // (a factor reset elsewhere) and the screen sends its code on the first try.
    if (isOperatorTwoFactorSetupRequired(refusal)) noteOperatorSetupRequired();
    noteIfConfirmationDemanded(refusal);
    if (
      error.config &&
      canHandOffToStepUpHost(error.config.stepUp, error.config.url) &&
      isHostAnswerableRefusal(refusal)
    ) {
      const original = error.config;
      return challengeStepUp<AxiosResponse>({
        kind: isOperatorTwoFactorSetupRequired(refusal)
          ? "setup-required"
          : "code-required",
        action: original.stepUpAction,
        resend: (twoFactorCode) =>
          axios.request({
            ...original,
            stepUp: "resend",
            // The prompt waits on this answer with its controls locked; a
            // stalled connection must end as "couldn't confirm", not hang.
            timeout: original.timeout || STEP_UP_RESEND_TIMEOUT_MS,
            headers: {
              ...plainHeaders(original.headers),
              ...stepUpCodeHeader(twoFactorCode),
            },
          }),
      });
    }

    // Handle specific HTTP status codes
    switch (status) {
      case 401:
        // 🔴 ONE renewal, ONE replay (src/auth/unauthorizedReplay.ts). The bearer
        // may simply have expired: renew it through the token provider (401s
        // arriving together share the one renewal, and a request that left with
        // a token already replaced just takes the current one) and send the
        // request again. The replay is marked, so a second 401 is rejected as
        // before and never renews again; a renewal that fails is rejected the
        // same way, and the session stays (a dropped connection is not a
        // sign-out). Only a refused BEARER is renewed: a 401 carrying any other
        // code (a wrong two-factor code) is about the request, and a request
        // carrying a one-time proof is never sent twice.
        if (isRefusedBearer(error)) {
          const original = error.config!;
          return bearerForReplay(original).then(
            (bearer) => axios.request(replayConfig(original, bearer)),
            () => rejectWithCode("Invalid credentials")
          );
        }
        return rejectWithCode("Invalid credentials");
      case 403:
        // Step-up 403s (TWO_FACTOR_REQUIRED) are actionable in place — the caller
        // prompts for the admin's 2FA code and retries — so they fall through to the
        // generic handling below, which preserves the server `code`.
        //
        // A genuine authz 403 is rejected INLINE — no global redirect. Route-level
        // access is already owned by the route guard: PrivateRoute +
        // getRequiredAdminFeatureForPath navigate a gated page to /unauthorized
        // before it renders, and a failed bootstrap sets AuthContext authStatus
        // "error" (→ same redirect). So a 403 that reaches this shared interceptor is
        // an ACTION-level denial on a single request (a scoped sub-permission, a
        // looped-in side-conversation participant, …). Rejecting it lets the
        // originating view surface a local "no access" state instead of
        // hard-navigating the WHOLE console to /unauthorized and discarding every
        // unsaved change. Mirrors the 401 case (which already rejects inline).
        //
        // 🔴 CARRYING THE SERVER'S REASON when it gave one. Every non-step-up 403
        // used to read "Access Forbidden" whatever the api said, so on 2026-09-21
        // two operators saving a role saw exactly that — while the api was
        // answering "You cannot grant capabilities you do not hold yourself:
        // referral.reward.reverse", the one sentence that said what to fix. The
        // generic copy is kept only for a 403 that arrives with no message.
        // Screens branch on `status` (see console/lib/apiError), never this text.
        if (!isStepUpErrorCode(code)) {
          const reason = forbiddenReasonOf(data);
          return rejectWithCode(reason || "Access Forbidden");
        }
        break;
      case 404:
        return rejectWithCode(
          "Sorry! the data you are looking for could not be found"
        );
      default:
        break;
    }

    // Extract error message from v2 format { success: false, message: "..." }
    // or legacy format { error: "...", message: "..." }
    let errorMessage: string;
    if (data && "success" in data && data.success === false) {
      errorMessage = (data as V2ErrorResponseBody).message;
    } else if (data?.message) {
      errorMessage = data.message;
    } else if (
      data &&
      "error" in data &&
      (data as LegacyErrorResponseBody).error
    ) {
      errorMessage = (data as LegacyErrorResponseBody).error as string;
    } else {
      errorMessage = error.message || "An unknown error occurred";
    }

    return rejectWithCode(errorMessage);
  }
);

/**
 * The headers the shared interceptors would put on a request, for the
 * `axios.create()` clients that bypass them (the two-factor clients, the
 * treasury and DPOS money ops, the CSV exports): the session's current bearer
 * from the token provider, plus the view-as preview header while one is armed,
 * so a previewed role stays read-only on those clients too. Nothing else reads
 * the Authorization header out of the axios defaults; an architecture test
 * fails if something does.
 */
const sharedRequestHeaders = async (): Promise<Record<string, string>> => {
  const defaults = getSafeAxiosDefaults();
  const viewAsRole = defaults.headers.common["X-View-As-Role"];
  const bearer = await currentBearer();
  return {
    ...(viewAsRole ? { "X-View-As-Role": viewAsRole } : {}),
    ...(bearer ? { Authorization: bearer } : {}),
  };
};

/**
 * Set (or clear) the default X-View-As-Role header for the super-admin role
 * switcher. When set, every request carries it and the server authorizes as that
 * previewed role (downgrade-only, read-only, enforced server-side). It rides on
 * `defaults.headers.common` for every call on this instance, and
 * sharedRequestHeaders() carries it to the clients that bypass the interceptors.
 * NOTE: clear it before calling POST /admin/view-as itself — that request must not
 * carry a preview header, or the server's read-only guard rejects it.
 */
const setViewAsRole = (roleKey?: string) => {
  const defaults = getSafeAxiosDefaults();

  if (roleKey) {
    defaults.headers.common["X-View-As-Role"] = roleKey;
  } else {
    delete defaults.headers.common["X-View-As-Role"];
  }
};

//function to get profile

// Fetch data with GET method
const get = <TResponse = LegacyApiResponse>(
  url: string,
  params?: QueryParams
) => {
  const queryString = params
    ? "?" + new URLSearchParams(toSearchParamsRecord(params)).toString()
    : "";
  return axios.get<TResponse>(url + queryString);
};

const getQuery = <TResponse = LegacyApiResponse>(
  url: string,
  params?: QueryParams,
  config: Omit<Parameters<typeof axios.get<TResponse>>[1], "params"> = {}
) => {
  return axios.get<TResponse>(url, { ...config, params });
};

// Fetch a file
const getFile = (url: string, params?: QueryParams) => {
  const queryString = params
    ? "?" + new URLSearchParams(toSearchParamsRecord(params)).toString()
    : "";
  return axios.get(url + queryString, { responseType: "blob" });
};

// Create a new resource with POST method
const create = <TResponse = LegacyApiResponse>(
  url: string,
  data?: RequestBody,
  config?: AxiosRequestConfig
) => {
  // The config exists only when a caller asked for one (e.g. a step-up opt-in,
  // ENG-1378), so every other caller posts with exactly the two arguments it
  // always has.
  return config
    ? axios.post<TResponse>(url, data, config)
    : axios.post<TResponse>(url, data);
};

// Update data with PATCH method
const updatePatch = <TResponse = LegacyApiResponse>(
  url: string,
  params: QueryParams = {},
  data: RequestBody = {},
  config: Parameters<typeof axios.patch<TResponse>>[2] = {}
) => {
  return axios.patch<TResponse>(url, data, {
    ...config,
    params, // Add query parameters here
  });
};

// Update data with PUT method
const update = (
  url: string,
  params: QueryParams = {},
  data: RequestBody = {},
  config: Parameters<typeof axios.put>[2] = {}
) => {
  //return axios.put(url, data);
  return axios.put(url, data, {
    ...config,
    params, // Add query parameters here
  });
};

// Delete a resource
const deleteResource = (
  url: string,
  params: QueryParams = {},
  data: RequestBody = {},
  config: Parameters<typeof axios.delete>[1] = {}
) => {
  //return axios.delete(url);
  return axios.delete(url, {
    ...config,
    params, // Query parameters should be included here
    data, // Body data (in case of DELETE, data might be required)
  });
};

// Create a resource with file upload
/*const createWithFile = (url, data) => {
  const formData = new FormData();
  /*for (const key in data) {
    formData.append(key, data[key]);
  }
  formData.append("data", JSON.stringify(data));

  return axios.post(url, formData, {
    headers: {
      ...axios.defaults.headers,
      "Content-Type": "multipart/form-data",
    },
  });
};*/
// const createWithFile = (url, data, files = []) => {
//   const formData = new FormData();
//   console.log("Files: ", files);

//   // Add the main data as a JSON string
//   formData.append("data", JSON.stringify(data));

//   // Add files to FormData
//   /*files.forEach((file, index) => {
//     formData.append(`files[${index}]`, file);
//   });*/
//   files.forEach((file) => {
//     formData.append("images", file /*.originFileObj*/); // Ensure original file object is appended
//   });

//   console.log(formData);

//   return axios.post(url, formData, {
//     headers: {
//       "Content-Type": "multipart/form-data",
//     },
//   });
// };

// const createWithFile = (url, data, files = []) => {
//   console.log("createWithFile", data);
//   const formData = new FormData();

//   files.forEach((file) => {
//     formData.append("images", file);
//   });

//   return axios.post(url, formData, {
//     headers: {
//       "Content-Type": "multipart/form-data",
//     },
//   });
// };

const createWithFile = (
  url: string,
  data?: RequestBody,
  files: File[] = []
) => {
  const formData = new FormData();

  const appendFormData = (
    fd: FormData,
    formValue: RequestBody,
    parentKey = ""
  ): void => {
    if (formValue instanceof FormData) {
      formValue.forEach((value, key) => {
        fd.append(key, value);
      });
      return;
    }

    if (
      formValue &&
      typeof formValue === "object" &&
      !(formValue instanceof File) &&
      !Array.isArray(formValue)
    ) {
      Object.entries(formValue).forEach(([key, value]) => {
        const formKey = parentKey ? `${parentKey}[${key}]` : key;
        appendFormData(fd, value as RequestBody, formKey);
      });
      return;
    }

    if (Array.isArray(formValue)) {
      formValue.forEach((value, index) => {
        const formKey = parentKey ? `${parentKey}[${index}]` : String(index);
        appendFormData(fd, value as RequestBody, formKey);
      });
      return;
    }

    if (formValue instanceof File) {
      fd.append(parentKey, formValue);
      return;
    }

    if (parentKey) {
      fd.append(parentKey, String(formValue ?? ""));
    }
  };

  appendFormData(formData, data);

  files.forEach((file) => {
    formData.append("images", file);
  });

  return axios.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

const updateWithFile = (url: string, data: RequestBody, files: File[] = []) => {
  const formData = new FormData();

  if (data instanceof FormData) {
    data.forEach((value, key) => {
      formData.append(key, value);
    });
  } else if (data && typeof data === "object") {
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, String(value ?? ""));
    });
  } else if (typeof data === "string") {
    formData.append("value", data);
  }

  files.forEach((file) => {
    formData.append("images", file);
  });

  return axios.patch(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// // Update with file upload
// const updateWithFile = (url, data) => {
//   const formData = new FormData();
//   for (const key in data) {
//     formData.append(key, data[key]);
//   }

//   return axios.patch(url, formData, {
//     headers: {
//       ...axios.defaults.headers,
//       "Content-Type": "multipart/form-data",
//     },
//   });
// };

// Add images to a resource with file upload
const addImages = (url: string, images: File[]) => {
  if (!Array.isArray(images) || images.length === 0) {
    throw new Error("images must be a non-empty array of files.");
  }

  const formData = new FormData();
  images.forEach((image, _index) => {
    formData.append("images", image); // Use dynamic keys for multiple images
  });

  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const axiosInstance = axios;

export {
  create,
  createWithFile,
  deleteResource,
  get,
  getFile,
  getQuery,
  setViewAsRole,
  sharedRequestHeaders,
  update,
  updatePatch,
  updateWithFile,
  addImages,
};
